/**
 * Sparkline - Compact inline trend chart
 *
 * Usage:
 *   <Sparkline data={{ values: [10, 12, 8, 15, 11, 14, 16] }} />
 */

import type { BlockProps } from './types';
import { CHART_COLORS } from './colors';
import './blocks.css';

export interface SparklineData {
  /** Array of numeric values */
  values: number[];
}

export interface SparklineProps extends BlockProps<SparklineData> {
  /** Width in pixels */
  width?: number;
  /** Height in pixels */
  height?: number;
  /** Line color */
  color?: string;
  /** Show area fill */
  showArea?: boolean;
  /** Show end dot */
  showEndDot?: boolean;
  /** Show trend percentage */
  showTrend?: boolean;
}

export function Sparkline({
  data,
  width = 120,
  height = 32,
  color = CHART_COLORS[0],
  showArea = true,
  showEndDot = true,
  showTrend = false,
  className,
}: SparklineProps) {
  const { values } = data;

  if (!values || values.length < 2) {
    return (
      <div
        className={className}
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-faint)',
          fontSize: '10px',
        }}
      >
        —
      </div>
    );
  }

  const padding = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  // Calculate points
  const points = values.map((value, index) => {
    const x = padding + (index / (values.length - 1)) * (width - padding * 2);
    const y = padding + (1 - (value - min) / range) * (height - padding * 2);
    return { x, y, value };
  });

  // Create SVG path
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Create area path
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding} L ${padding} ${height - padding} Z`;

  // Calculate trend
  const firstHalf = values.slice(0, Math.floor(values.length / 2));
  const secondHalf = values.slice(Math.floor(values.length / 2));
  const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
  const trendPct = firstAvg !== 0
    ? ((secondAvg - firstAvg) / firstAvg) * 100
    : secondAvg > 0 ? 100 : 0;

  return (
    <div className={`sparkline ${className || ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        style={{ overflow: 'visible' }}
      >
        {/* Area fill */}
        {showArea && (
          <path
            d={areaPath}
            fill={color}
            fillOpacity={0.1}
          />
        )}

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* End dot */}
        {showEndDot && (
          <circle
            cx={points[points.length - 1].x}
            cy={points[points.length - 1].y}
            r={3}
            fill={color}
          />
        )}
      </svg>

      {showTrend && (
        <span
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: trendPct > 0 ? 'var(--color-success)' : trendPct < 0 ? 'var(--color-danger)' : 'var(--color-text-muted)',
          }}
        >
          {trendPct > 0 ? '↑' : trendPct < 0 ? '↓' : '→'}
          {Math.abs(trendPct).toFixed(0)}%
        </span>
      )}
    </div>
  );
}

export default Sparkline;
